import { useRef } from "react"
import { HugeiconsIcon } from "@hugeicons/react"
import { FlipHorizontalIcon, FlipVerticalIcon } from "@hugeicons/core-free-icons"
import { useCompositorStore } from "@/lib/stores/compositor-store"
import { usePipelineStore } from "@/lib/stores/pipeline-store"
import { AlignmentButtons } from "./AlignmentButtons"
import { FontPicker } from "./FontPicker"
import { BLEND_MODES, DEFAULT_TEXT_STYLE } from "@/lib/types/compositor"
import { cn } from "@/lib/utils"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const TEXT_MODULES = ["text_input", "llm_text", "text_builder"]
const FONT_SIZE_PRESETS = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 96, 120, 144]

// Reusable numeric field
function NumberField({
  label,
  value,
  onChange,
  suffix,
  className,
}: {
  label: string
  value: number
  onChange: (v: number) => void
  suffix?: string
  className?: string
}) {
  return (
    <div className={cn("flex flex-col gap-1", className)}>
      <span className="text-[10px] text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="h-7 w-full px-2 rounded border border-border/50 bg-input text-[12px] outline-none focus:border-ring"
        />
        {suffix && <span className="text-[10px] text-muted-foreground shrink-0">{suffix}</span>}
      </div>
    </div>
  )
}

// Color + opacity fill field with native color picker
function FillField({ fill, fillOpacity, onFillChange, onOpacityChange }: {
  fill: string
  fillOpacity: number
  onFillChange: (hex: string) => void
  onOpacityChange: (v: number) => void
}) {
  const colorInputRef = useRef<HTMLInputElement>(null)
  return (
    <div>
      <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Fill</label>
      <div className="flex gap-2 items-center">
        <div className="relative shrink-0 cursor-pointer" onClick={() => colorInputRef.current?.click()}>
          <div
            className="w-6 h-6 rounded border border-border/50"
            style={{ backgroundColor: `#${fill}` }}
          />
          <input
            ref={colorInputRef}
            type="color"
            value={`#${fill.length === 6 ? fill : "ffffff"}`}
            onChange={(e) => onFillChange(e.target.value.replace("#", ""))}
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
            tabIndex={-1}
          />
        </div>
        <input
          type="text"
          value={fill}
          onChange={(e) => onFillChange(e.target.value.replace("#", ""))}
          maxLength={6}
          className="h-7 flex-1 min-w-0 px-2 rounded border border-border/50 bg-input text-[12px] font-mono outline-none focus:border-ring"
        />
        <div className="flex items-center gap-0.5 shrink-0">
          <input
            type="number"
            value={fillOpacity}
            min={0}
            max={100}
            onChange={(e) => onOpacityChange(Number(e.target.value))}
            className="h-7 w-10 px-1 rounded border border-border/50 bg-input text-[12px] outline-none focus:border-ring text-center"
          />
          <span className="text-[10px] text-muted-foreground">%</span>
        </div>
      </div>
    </div>
  )
}

const FONT_WEIGHTS = ["Thin", "Light", "Regular", "Medium", "Semi Bold", "Bold", "Extra Bold"]
const FONT_STYLES = ["Normal", "Italic"]

// Font size field with dropdown presets + free-form input
function FontSizeField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  return (
    <div className="flex flex-col gap-1 flex-1">
      <span className="text-[10px] text-muted-foreground">Size</span>
      <div className="flex items-center gap-1">
        <input
          type="text"
          inputMode="numeric"
          list="compositor-font-sizes"
          value={value}
          onChange={(e) => { const n = parseInt(e.target.value, 10); if (n > 0) onChange(n) }}
          className="h-7 w-full px-2 rounded border border-border/50 bg-input text-[12px] outline-none focus:border-ring"
        />
        <datalist id="compositor-font-sizes">
          {FONT_SIZE_PRESETS.map((s) => <option key={s} value={s} />)}
        </datalist>
        <span className="text-[10px] text-muted-foreground shrink-0">px</span>
      </div>
    </div>
  )
}
const TEXT_ALIGNS = ["left", "center", "right"] as const
const VERTICAL_ALIGNS = ["top", "center", "bottom"] as const

export function CompositorInspector() {
  const config = useCompositorStore((s) => s.config)
  const nodeId = useCompositorStore((s) => s.nodeId)
  const selectedItem = useCompositorStore((s) => s.selectedItem)
  const updateCanvas = useCompositorStore((s) => s.updateCanvas)
  const updateTransform = useCompositorStore((s) => s.updateTransform)
  const updateTextStyle = useCompositorStore((s) => s.updateTextStyle)
  const updateLayer = useCompositorStore((s) => s.updateLayer)

  const pipelineEdges = usePipelineStore((s) => s.edges)
  const pipelineNodes = usePipelineStore((s) => s.nodes)

  if (!config) return null

  const layer = selectedItem?.type === "layer"
    ? config.layers.find((l) => l.id === selectedItem.layerId)
    : null

  // A layer is "text" if its type is text OR if it's connected to a text source node
  const isTextLayer = layer && (
    layer.type === "text" ||
    (() => {
      if (!nodeId) return false
      const edge = pipelineEdges.find((e) => e.target === nodeId && e.targetHandle === layer.id)
      if (!edge) return false
      const src = pipelineNodes.find((n) => n.id === edge.source)
      return src ? TEXT_MODULES.includes(src.data.module) : false
    })()
  )

  // Use defaults when textStyle is not yet initialized on the layer
  const textStyle = layer?.textStyle ?? DEFAULT_TEXT_STYLE

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="px-3 py-2 border-b border-border/50 shrink-0">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
          {layer ? layer.name : "Canvas"}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4">
        {!layer ? (
          // Canvas inspector
          <>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Dimensions</label>
              <div className="flex gap-2">
                <NumberField
                  label="W"
                  value={config.canvas.width}
                  onChange={(v) => updateCanvas({ width: v })}
                  className="flex-1"
                />
                <NumberField
                  label="H"
                  value={config.canvas.height}
                  onChange={(v) => updateCanvas({ height: v })}
                  className="flex-1"
                />
              </div>
            </div>
            {config.layers.some((l) => l.type === "video") && (
              <NumberField
                label="Duration"
                value={config.canvas.duration}
                onChange={(v) => updateCanvas({ duration: v })}
                suffix="s"
              />
            )}
          </>
        ) : (
          // Layer inspector
          <>
            {/* Position */}
            {layer.type !== "background" && (
              <>
                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Position</label>
                  <div className="flex gap-2">
                    <NumberField label="X" value={Math.round(layer.transform.x)} onChange={(v) => updateTransform(layer.id, { x: Math.round(v) })} className="flex-1" />
                    <NumberField label="Y" value={Math.round(layer.transform.y)} onChange={(v) => updateTransform(layer.id, { y: Math.round(v) })} className="flex-1" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Dimensions</label>
                  <div className="flex gap-2">
                    <NumberField label="W" value={Math.round(layer.transform.width)} onChange={(v) => updateTransform(layer.id, { width: Math.round(v) })} className="flex-1" />
                    <NumberField label="H" value={Math.round(layer.transform.height)} onChange={(v) => updateTransform(layer.id, { height: Math.round(v) })} className="flex-1" />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Alignment</label>
                  <AlignmentButtons
                    transform={layer.transform}
                    canvas={config.canvas}
                    onAlign={(patch) => updateTransform(layer.id, patch)}
                  />
                </div>

                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Rotation</label>
                  <div className="flex items-end gap-2">
                    <NumberField label="" value={layer.transform.rotation} onChange={(v) => updateTransform(layer.id, { rotation: v })} suffix="°" className="w-20" />
                    <div className="flex gap-1 mb-0">
                      <button
                        onClick={() => updateTransform(layer.id, { flipH: !layer.transform.flipH })}
                        title="Flip horizontal"
                        className={cn(
                          "h-7 w-7 flex items-center justify-center rounded border border-border/50 transition-colors",
                          layer.transform.flipH ? "bg-accent border-border" : "hover:bg-accent"
                        )}
                      >
                        <HugeiconsIcon icon={FlipHorizontalIcon} size={13} className="text-muted-foreground" />
                      </button>
                      <button
                        onClick={() => updateTransform(layer.id, { flipV: !layer.transform.flipV })}
                        title="Flip vertical"
                        className={cn(
                          "h-7 w-7 flex items-center justify-center rounded border border-border/50 transition-colors",
                          layer.transform.flipV ? "bg-accent border-border" : "hover:bg-accent"
                        )}
                      >
                        <HugeiconsIcon icon={FlipVerticalIcon} size={13} className="text-muted-foreground" />
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}

            <NumberField label="Opacity" value={layer.opacity} onChange={(v) => updateLayer(layer.id, { opacity: v })} suffix="%" />

            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Blend Mode</label>
              <Select value={layer.blendMode} onValueChange={(v) => updateLayer(layer.id, { blendMode: v })}>
                <SelectTrigger className="h-7 text-[12px] border-border/50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {BLEND_MODES.map((m) => (
                    <SelectItem key={m} value={m} className="text-[12px]">{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Text-only controls — shown when layer type is text OR connected to a text source */}
            {isTextLayer && (
              <>
                <div className="pt-2 border-t border-border/30">
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Font</label>
                  <FontPicker
                    value={textStyle.fontFamily}
                    onChange={(v) => updateTextStyle(layer.id, { fontFamily: v })}
                  />
                </div>

                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground block mb-1">Weight</label>
                    <Select
                      value={textStyle.fontWeight}
                      onValueChange={(v) => updateTextStyle(layer.id, { fontWeight: v })}
                    >
                      <SelectTrigger className="h-7 text-[12px] border-border/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_WEIGHTS.map((w) => (
                          <SelectItem key={w} value={w} className="text-[12px]">{w}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <label className="text-[10px] text-muted-foreground block mb-1">Style</label>
                    <Select
                      value={textStyle.fontStyle}
                      onValueChange={(v) => updateTextStyle(layer.id, { fontStyle: v })}
                    >
                      <SelectTrigger className="h-7 text-[12px] border-border/50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {FONT_STYLES.map((s) => (
                          <SelectItem key={s} value={s} className="text-[12px]">{s}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex gap-2">
                  <FontSizeField value={textStyle.fontSize} onChange={(v) => updateTextStyle(layer.id, { fontSize: v })} />
                  <NumberField label="Line H" value={textStyle.lineHeight} onChange={(v) => updateTextStyle(layer.id, { lineHeight: v })} suffix="%" className="flex-1" />
                </div>

                <NumberField label="Letter Spacing" value={textStyle.letterSpacing} onChange={(v) => updateTextStyle(layer.id, { letterSpacing: v })} suffix="%" />

                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Text Align</label>
                  <div className="flex gap-1">
                    {TEXT_ALIGNS.map((a) => (
                      <button
                        key={a}
                        onClick={() => updateTextStyle(layer.id, { textAlign: a })}
                        className={cn(
                          "flex-1 h-7 text-[11px] rounded border border-border/50 capitalize transition-colors",
                          textStyle.textAlign === a ? "bg-accent border-border" : "hover:bg-accent"
                        )}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] text-muted-foreground uppercase tracking-wider block mb-2">Vertical Align</label>
                  <div className="flex gap-1">
                    {VERTICAL_ALIGNS.map((a) => (
                      <button
                        key={a}
                        onClick={() => updateTextStyle(layer.id, { verticalAlign: a })}
                        className={cn(
                          "flex-1 h-7 text-[11px] rounded border border-border/50 capitalize transition-colors",
                          textStyle.verticalAlign === a ? "bg-accent border-border" : "hover:bg-accent"
                        )}
                      >
                        {a}
                      </button>
                    ))}
                  </div>
                </div>

                <FillField
                  fill={textStyle.fill}
                  fillOpacity={textStyle.fillOpacity}
                  onFillChange={(v) => updateTextStyle(layer.id, { fill: v })}
                  onOpacityChange={(v) => updateTextStyle(layer.id, { fillOpacity: v })}
                />
              </>
            )}
          </>
        )}
      </div>
    </div>
  )
}
